import { useId, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { HugeiconsIcon } from '@hugeicons/react'
import { AlertCircleStrokeRounded, Attachment01StrokeRounded } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetOrganisationUsersQuery } from '@/app/services/organisationUsers'
import { selectLocale } from '@/app/slices/appSlice'
import { TbxAlertDialog } from '@/common/components'
import {
    ATTACHMENT_MAX_FILE_BYTES,
    ATTACHMENT_MAX_PER_PARENT,
    ATTACHMENT_SCAN_STATE,
} from '@/common/utils/Constants/Constants'

import QuoteAttachmentRow, { ATTACHMENT_ROW_COLUMNS, ATTACHMENT_TABLE_MIN_WIDTH } from './QuoteAttachmentRow'
import UploadRow from './UploadRow'
import UploadZone from './UploadZone'
import useAttachmentUploads, { UPLOAD_STATE } from './useAttachmentUploads'

const errorMessage = (error, fallback) => (typeof error?.data === 'string' && error.data ? error.data : fallback)

// The API sends attachment refusals as text/plain. RTK Query cannot parse that as JSON, so it
// reports a PARSING_ERROR and keeps the HTTP status in originalStatus.
const httpStatus = (error) => error?.originalStatus ?? error?.status

/**
 * AttachmentsPanel - The documents ledger for one parent (a quote, a quote item, ...). It owns
 * the dropzone, rows, confirm dialogs and error messages; the caller owns fetching and the API
 * calls, so each parent type wires its own endpoints. Rendering is gated by the caller.
 *
 * @param {Object} props
 * @param {Array} props.attachments - DocumentDto rows
 * @param {string} props.emptyMessage - Shown when there are no rows
 * @param {string} props.organisationId - Used to resolve uploader names
 * @param {boolean} [props.isDeleting]
 * @param {Function} props.onDelete - (documentId) => Promise, rejects with the RTK Query error
 * @param {Function} props.onSetVisibility - (documentId, isPublic) => Promise, rejects likewise
 * @param {Function} props.onUploadFile - (file, { onProgress }) => Promise for one file, see
 *   useAttachmentUploads
 * @param {Function} props.onUploadsSettled - Refreshes the rows once a batch of uploads is done
 * @param {string} props.title - Names the level, e.g. "Part attachments", so users see where a file goes
 * @param {Function} props.resolveDownloadUri - (attachment) => Promise<string|undefined>; undefined
 *   means not ready yet, a rejection means the link could not be fetched
 */
const AttachmentsPanel = ({
    attachments,
    emptyMessage,
    isDeleting = false,
    onDelete,
    onSetVisibility,
    onUploadFile,
    onUploadsSettled,
    organisationId,
    resolveDownloadUri,
    title,
}) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const locale = useSelector(selectLocale)

    const { data: organisationUsers } = useGetOrganisationUsersQuery({ organisationId })

    const [busyDocumentId, setBusyDocumentId] = useState(null)
    const [attachmentToPublish, setAttachmentToPublish] = useState(null)
    const [attachmentToDelete, setAttachmentToDelete] = useState(null)

    const uploaderName = (createdBy) => {
        const user = organisationUsers?.find(({ userId }) => userId === createdBy)
        return user ? `${user.firstName} ${user.lastName}`.trim() : undefined
    }

    // Staff-only and customer-visible documents each have their own slots. Uploads from here are
    // always staff only, so only the staff slots limit adding files.
    const staffAttachments = attachments.filter(({ isPublic }) => !isPublic)
    const customerCount = attachments.length - staffAttachments.length
    const isCustomerFull = customerCount >= ATTACHMENT_MAX_PER_PARENT

    const uploads = useAttachmentUploads({ usedSlots: staffAttachments.length, onUploadFile, onUploadsSettled })
    const isFull = uploads.freeSlots === 0
    const rowIdPrefix = useId()
    const uploadRowId = (key) => `${rowIdPrefix}-upload-${key}`

    const problems = uploads.items.filter(({ state }) => state === UPLOAD_STATE.error).length
    const jumpToFirstProblem = () => {
        const first = uploads.items.find(({ state }) => state === UPLOAD_STATE.error)
        if (first) document.getElementById(uploadRowId(first.key))?.scrollIntoView({ block: 'nearest' })
    }

    // Header pips show the staff slots, the ones uploads fill: stored, not accepted, on the way, free.
    const staffCount = staffAttachments.length
    const failedCount = staffAttachments.filter(({ scanState }) => scanState === ATTACHMENT_SCAN_STATE.Failed).length
    const sendingCount = uploads.items.filter(({ state }) =>
        [UPLOAD_STATE.waiting, UPLOAD_STATE.uploading, UPLOAD_STATE.checking, UPLOAD_STATE.done].includes(state)
    ).length
    // Files on their way in already hold a staff slot, so a switch to staff must not take it.
    const isStaffFull = staffCount + sendingCount >= ATTACHMENT_MAX_PER_PARENT
    const pips = Array.from({ length: ATTACHMENT_MAX_PER_PARENT }, (_, index) => {
        if (index < staffCount - failedCount) return 'text.primary'
        if (index < staffCount) return 'error.main'
        if (index < staffCount + sendingCount) return 'primary.main'
        return 'action.disabledBackground'
    })

    // No `accept` on purpose: any file type may be uploaded, the server scans it.
    const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
        disabled: uploads.inFlight || isFull,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        onDrop: (files) => files.length && uploads.addFiles(files),
    })

    const applyVisibility = async (attachment, isPublic) => {
        setBusyDocumentId(attachment.documentId)
        try {
            await onSetVisibility(attachment.documentId, isPublic)
        } catch (error) {
            enqueueSnackbar(errorMessage(error, t('Failed to change attachment visibility')), { variant: 'error' })
        } finally {
            setBusyDocumentId(null)
        }
    }

    const handleToggleVisibility = (attachment, isPublic) => {
        // Going public is the consequential direction: confirm it. Going private is not.
        if (isPublic) {
            setAttachmentToPublish(attachment)
            return
        }
        applyVisibility(attachment, false)
    }

    const handleConfirmPublish = async () => {
        const attachment = attachmentToPublish
        setAttachmentToPublish(null)
        await applyVisibility(attachment, true)
    }

    const handleConfirmDelete = async () => {
        const attachment = attachmentToDelete
        try {
            await onDelete(attachment.documentId)
        } catch (error) {
            if (httpStatus(error) === 409) {
                enqueueSnackbar(t('This file is still being checked. Try again shortly.'), { variant: 'warning' })
            } else {
                enqueueSnackbar(errorMessage(error, t('Failed to delete attachment')), { variant: 'error' })
            }
        } finally {
            setAttachmentToDelete(null)
        }
    }

    const handleDownload = async (attachment) => {
        // Open the tab now, inside the click: popup blockers only allow window.open during the
        // user's gesture, and the link may need an async fetch first. (No 'noopener' here, it
        // would return null; the opener is cut below instead.)
        const tab = window.open('', '_blank')
        if (!tab) {
            enqueueSnackbar(t('Your browser blocked the download. Allow pop-ups for this site and try again.'), {
                variant: 'warning',
            })
            return
        }

        let uri
        try {
            uri = await resolveDownloadUri(attachment)
        } catch (error) {
            tab.close()
            enqueueSnackbar(errorMessage(error, t('Could not get the download link. Try again.')), {
                variant: 'error',
            })
            return
        }
        if (!uri) {
            tab.close()
            enqueueSnackbar(t('This file is not ready to download yet.'), { variant: 'warning' })
            return
        }

        // Keeps the new tab from reaching back into the app, as noopener would.
        tab.opener = null
        tab.location.href = uri
    }

    return (
        <Box
            {...getRootProps({
                'aria-label': title,
                component: 'section',
                // react-dropzone sets role="presentation"; this is a landmark, not decoration.
                role: 'region',
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                },
            })}
        >
            <input {...getInputProps({ 'aria-label': t('Upload attachments') })} />

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'space-between',
                    mx: 1.5,
                }}
            >
                <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography
                        sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.75, textTransform: 'uppercase' }}
                        variant="strong1"
                    >
                        <HugeiconsIcon
                            icon={Attachment01StrokeRounded}
                            size={20}
                        />
                        {title}
                    </Typography>
                    <Box
                        sx={{ display: 'inline-flex', gap: 0.5 }}
                        aria-hidden
                    >
                        {pips.map((color, index) => (
                            <Box
                                // Five fixed slots; the index is the identity.
                                key={index}
                                sx={{ bgcolor: color, borderRadius: 1, height: 8, width: 22 }}
                            />
                        ))}
                    </Box>
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        {t(
                            'Staff {{staff}} of {{max}} · Customer {{customer}} of {{max}} · {{maxSize}} MB per file · any type',
                            {
                                staff: staffCount,
                                customer: customerCount,
                                max: ATTACHMENT_MAX_PER_PARENT,
                                maxSize: Math.round(ATTACHMENT_MAX_FILE_BYTES / (1024 * 1024)),
                            }
                        )}
                    </Typography>
                </Box>
                {problems ? (
                    <Button
                        color="error"
                        size="small"
                        startIcon={
                            <HugeiconsIcon
                                icon={AlertCircleStrokeRounded}
                                size={16}
                            />
                        }
                        sx={{ borderRadius: 4, py: 0 }}
                        variant="outlined"
                        onClick={jumpToFirstProblem}
                    >
                        {problems === 1 ? t('1 needs attention') : t('{{count}} need attention', { count: problems })}
                    </Button>
                ) : null}
            </Box>

            <UploadZone
                batchKeys={uploads.batchKeys}
                excess={uploads.excess}
                freeSlots={uploads.freeSlots}
                heldCount={uploads.heldCount}
                inFlight={uploads.inFlight}
                isDragActive={isDragActive}
                items={uploads.items}
                onAdd={open}
                onSendHeld={uploads.sendHeld}
            />

            {attachments.length || uploads.items.length ? (
                // Only the table scrolls sideways; the title and the drop strip keep the card's width.
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        aria-label={t('Attachment list')}
                        component="ul"
                        sx={{ m: 0, minWidth: ATTACHMENT_TABLE_MIN_WIDTH, p: 0 }}
                    >
                        <Box
                            role="row"
                            sx={{
                                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                color: 'text.secondary',
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: ATTACHMENT_ROW_COLUMNS,
                                px: 1.5,
                                pb: 1,
                            }}
                        >
                            {[t('Name'), t('Uploaded'), t('Visibility'), t('Status')].map((label) => (
                                <Typography
                                    key={label}
                                    role="columnheader"
                                    sx={{ fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                                    variant="caption"
                                >
                                    {label}
                                </Typography>
                            ))}
                            <span />
                        </Box>
                        {attachments.map((attachment) => (
                            <QuoteAttachmentRow
                                attachment={attachment}
                                busy={busyDocumentId === attachment.documentId}
                                isDestinationFull={attachment.isPublic ? isStaffFull : isCustomerFull}
                                key={attachment.documentId}
                                locale={locale}
                                uploaderName={uploaderName(attachment.createdBy)}
                                onDelete={setAttachmentToDelete}
                                onDownload={handleDownload}
                                onToggleVisibility={handleToggleVisibility}
                            />
                        ))}
                        {/* Files on their way in sit in the same table and keep their place. */}
                        {uploads.items.map((item) => (
                            <UploadRow
                                freeSlots={uploads.freeSlots}
                                id={uploadRowId(item.key)}
                                inFlight={uploads.inFlight}
                                item={item}
                                key={item.key}
                                onRemove={uploads.remove}
                                onRetry={uploads.retry}
                            />
                        ))}
                    </Box>
                </Box>
            ) : (
                <Typography
                    color="text.secondary"
                    sx={{ mx: 1.5 }}
                >
                    {emptyMessage}
                </Typography>
            )}

            <TbxAlertDialog
                content={
                    <>
                        <strong>{attachmentToPublish?.documentName}</strong>
                        {' — '}
                        {t(
                            'this file will become visible to the customer in the Web Store, and they will be able to download it.'
                        )}
                    </>
                }
                okButtonText={t('Make visible')}
                open={Boolean(attachmentToPublish)}
                title={t('Make this attachment visible to the customer?')}
                onCancelClose={() => setAttachmentToPublish(null)}
                onOkClose={handleConfirmPublish}
            />

            <TbxAlertDialog
                content={
                    <>
                        <strong>{attachmentToDelete?.documentName}</strong>
                        {' — '}
                        {t('this cannot be undone.')}
                    </>
                }
                loading={isDeleting}
                okButtonText={t('Delete')}
                open={Boolean(attachmentToDelete)}
                title={t('Delete attachment?')}
                onCancelClose={() => setAttachmentToDelete(null)}
                onOkClose={handleConfirmDelete}
            />
        </Box>
    )
}

AttachmentsPanel.propTypes = {
    attachments: PropTypes.array.isRequired,
    emptyMessage: PropTypes.string.isRequired,
    organisationId: PropTypes.string.isRequired,
    resolveDownloadUri: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSetVisibility: PropTypes.func.isRequired,
    onUploadFile: PropTypes.func.isRequired,
    onUploadsSettled: PropTypes.func.isRequired,
    isDeleting: PropTypes.bool,
}

export default AttachmentsPanel
